import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Observable, throwError  } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
@Injectable({
  providedIn: 'root'
})
export class StrapiService {
  private apiUrl = environment.apiURL + '/api';
  private auth_token = environment.strapiAUTH;
  private headers = new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${this.auth_token}`
  });
  private userToken: string;
  
  constructor(private http: HttpClient, private authService: AuthService ) {
      this.authService.userToken$.subscribe((token) => {
        this.userToken = token;
      });
    }
/* 
  Strapi basic API example: 
  http://localhost:1337/api/your-content-type-plural?filters[your-slug-field-name][operator]=your-slug-goes-here
*/

  processSecureAPICall( url: string ): Observable<any> {
    
    const dynamicHeaders = this.headers.set('User-Token', this.userToken || '');

    return this.http.get<any>(
      url,
      { headers: dynamicHeaders }
    ).pipe(
      catchError(err => {
        if (err.status === 401) {
          this.authService.logout();
        }
        return throwError(() => err);
      })
    );
  }

  getItemBySlug( contentType: string, slug: string ) : Observable<any> {
    return this.processSecureAPICall(`${this.apiUrl}/${contentType}?filters[Slug][$eq]=${slug}`);
  }

  getEndpointDetails( slug: string ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/endpoints?filters[Slug][$eq]=${slug}&populate[0]=DomainVersionActive&populate[1]=Group` );
  }

  getActiveVersion( slug: string ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/endpoints/activeVersions/${slug}` );
  }

  getSideNavByVersion( version: number ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/side-nav/${version}` );
  }

  getAllGroups( version: number ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/endpoints/groups/${version}` );
  }

  getActiveRecipes( ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/recipes?filters[Active][$eq]=true&populate[0]=Icon&populate[1]=Steps` );
  }

  getCodeSnippet( version: number, language:string, slug:string ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/codesnippets/${version}/${language}/${slug}` );
  }

  getCodelibraries(type:string) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/codelibraries/type/${type}` );
  }

  getAllCodelibraries() : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/codelibraries/librarytype/1` );
  }

  getRecipeBySlug( slug: string ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/recipes?filters[Slug][$eq]=${slug}&populate[0]=Icon&populate[Steps][populate][1]=Endpoint` );
  }

  getRecipesByEndpoint( slug: string ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/recipes/by-endpoint/${slug}` );
  }

    getNotesByStep( noteType: string, step:string ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/notes/step/${step}/${noteType}` );
  }

  getSearchResults( version: number, query: string, groupFilter?: Array<any>, typeFilter?: Array<any> ) : Observable<any> {
    let sanitizedQuery = query.replace(/\//g, "~~");
    let groups = ( groupFilter && groupFilter.length ) ? groupFilter : 'all' ;
    let types = ( typeFilter && typeFilter.length ) ? typeFilter : 'all' ;
    return this.processSecureAPICall( `${this.apiUrl}/search-results/${version}/${sanitizedQuery}/${groups}/${types}` );
  }

  getEndpointParameters( slug: string, version: number ) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/parameter-processed/${slug}/${version}` );
  }

  getQueryHeaderParams( slug: string, version: number) : Observable<any> {
    return this.processSecureAPICall( `${this.apiUrl}/parameter-processed/query-header-params/${slug}/${version}` );
  }

  getEndpointResponses( slug: string, version:number ) : Observable<any> {
    // define url to pull response and param data
    let urlAddition = `/responses?filters[Active]=true&filters[Endpoint][Slug][$eq]=${slug}&filters[DomainVersion][Version][$eq]=${version}&populate[ResponseParameters][populate][Children][populate][Children][populate]=Children`;
    
    // add sorting for params and their children
    urlAddition+=`&populate[ResponseParameters][sort]=Name:asc&populate[ResponseParameters][populate][Children][sort]=Name:asc&populate[ResponseParameters][populate][Children][populate][Children][sort]=Name:asc`;
    
    // execute and return the response
    return this.processSecureAPICall( `${this.apiUrl}${urlAddition}` );
  }
    
  login( credentials: { username: string; password: string; token: string; }): Observable<any> {
    return this.http.post (`${this.apiUrl}/user/login`, credentials, { headers: this.headers });
  }

  logout( ): Observable<any> {
    const payload = { token: this.userToken };
    return this.http.post (`${this.apiUrl}/user/logout`, payload, { headers: this.headers });
  }

  sendEmail( formData: any, token: string ) : Observable<any> {
    formData.token = token;
    return this.http.post<any>(`${this.apiUrl}/user/request-access`, formData, { headers: this.headers });
  }

  sendPasswordReset( formData: any, token: string ) : Observable<any> {
    formData.token = token;
    return this.http.post<any>(`${this.apiUrl}/user/reset`, formData, { headers: this.headers });
  }

  
}
